import React, { useEffect, useMemo, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { useMembers } from "../../../data/membersStore";
import useLockBodyScroll from "../../../hooks/useLockBodyScroll";
import "./newAppointment.css";
import "./packageFlow.css";

// Same deterministic name -> color avatar as the Members screen (Members/index.js) — reused
// here (not the old gender-based blue/pink MUI Avatar NewAppointmentFlow.jsx uses) so this
// popup's member rows match the rest of the app's current look instead of an older pattern.
const AVATAR_COLORS = ["#2563EB", "#10B981", "#F59E0B", "#6366F1", "#EC4899", "#14B8A6"];

const colorForName = (name) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

// Fixed A → Š sort by first name — no control to change it (matches the Members page's own
// default sort). Serbian collation so Š/Đ/Č/Ć/Ž land in their correct alphabetical position.
const nameCollator = new Intl.Collator("sr", { sensitivity: "base" });
const byFirstName = (a, b) => nameCollator.compare(a.first_name || "", b.first_name || "");

// Same responsive convention as every other popup in this app (NewAppointmentFlow,
// PackageAssignFlow, ProfilePhotoPicker, ...): Tablet/Desktop get a centered modal, Mobile
// gets a bottom sheet.
const useIsDesktopTablet = () => {
  const [isDesktopTablet, setIsDesktopTablet] = useState(
    typeof window !== "undefined" ? window.innerWidth >= 768 : false
  );
  useEffect(() => {
    const onResize = () => setIsDesktopTablet(window.innerWidth >= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return isDesktopTablet;
};

// Reuses the exact same shared member cache (membersStore.js) and name/email/phone search
// used by NewAppointmentFlow.jsx's "Izaberite člana" step and the Members screen's own search
// bar — same data source and filter convention, just packaged as a standalone popup so it can
// be opened as a second, stacked layer on top of PackageAssignFlow's confirm step (for
// multi-vežbač member selection) without duplicating either the fetch or the filter logic.
const MemberSelectorPopup = ({
  open,
  title = "Izaberite člana",
  excludeIds = [],
  onClose,
  onSelect,
}) => {
  const isDesktopTablet = useIsDesktopTablet();
  useLockBodyScroll(open);
  const { clients, loading } = useMembers();
  const [search, setSearch] = useState("");
  // Mobile only: drives the sheet's keyboard-open repositioning off the search input's own
  // focus/blur (see the matching NewAppointmentFlow.jsx comment) rather than any
  // viewport-resize signal — window.visualViewport does not reliably fire its resize event on
  // keyboard open in Chrome for iOS, so that approach alone left results hidden behind the
  // keyboard there.
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => {
    if (open) setSearch("");
  }, [open]);

  useEffect(() => {
    if (!isDesktopTablet || !open) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isDesktopTablet, open, onClose]);

  const excludeSet = useMemo(
    () => new Set(excludeIds.filter((id) => id != null)),
    [excludeIds]
  );

  const filteredClients = useMemo(() => {
    const pool = clients.filter((c) => !excludeSet.has(c.id));
    const q = search.trim().toLowerCase();
    const matched = q
      ? pool.filter((c) => {
          const haystack = `${c.first_name || ""} ${c.last_name || ""} ${
            c.email || ""
          } ${c.phone || ""}`.toLowerCase();
          return haystack.includes(q);
        })
      : pool;
    return [...matched].sort(byFirstName);
  }, [clients, excludeSet, search]);

  if (!open) return null;

  const searchBar = (
    <div className="hf-naf-search">
      <SearchIcon sx={{ fontSize: 18 }} />
      <input
        type="text"
        placeholder="Ime, email ili telefon..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        onFocus={() => setSearchFocused(true)}
        onBlur={() => setSearchFocused(false)}
        autoFocus={isDesktopTablet}
      />
    </div>
  );

  const resultsList =
    loading && clients.length === 0 ? (
      <div className="hf-naf-empty">Učitavanje članova...</div>
    ) : filteredClients.length === 0 ? (
      <div className="hf-naf-empty">Nema pronađenih članova.</div>
    ) : (
      <div className="hf-naf-member-list">
        {filteredClients.map((client) => {
          const fullName = `${client.first_name || ""} ${client.last_name || ""}`.trim();
          const initials = `${(client.first_name || "")[0] || ""}${
            (client.last_name || "")[0] || ""
          }`.toUpperCase();
          return (
            <div
              key={client.id}
              className="hf-naf-member-row"
              // Keeps the search input focused through the tap — see the matching comment on
              // NewAppointmentFlow.jsx's MemberResultRow.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => onSelect(client)}
            >
              <div
                className="hf-pkg-member-avatar"
                style={
                  client.photo_url
                    ? undefined
                    : { background: colorForName(fullName || "?") }
                }
              >
                {client.photo_url ? (
                  <img src={client.photo_url} alt="" className="hf-pkg-member-avatar-img" />
                ) : (
                  initials || "?"
                )}
              </div>
              <div className="hf-naf-member-info">
                <span className="hf-naf-member-name">{fullName || "Bez imena"}</span>
                <span className="hf-naf-member-contact">
                  {client.email || client.phone || ""}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );

  const headerRow = (
    <div className="hf-pkg-header">
      <div className="hf-pkg-header-row">
        <h2 className="hf-pkg-title">{title}</h2>
      </div>
      <button type="button" className="hf-pkg-close" onClick={onClose} aria-label="Zatvori">
        <CloseIcon sx={{ fontSize: 16 }} />
      </button>
    </div>
  );

  if (isDesktopTablet) {
    return (
      <div className="hf-pkg-overlay hf-pkg-overlay-stacked">
        <div className="hf-pkg-modal hf-pkg-modal-picker">
          {headerRow}
          {searchBar}
          <div className="hf-pkg-scroll-body">{resultsList}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="hf-pkg-overlay hf-pkg-overlay-stacked">
      <div
        className={`hf-pkg-sheet hf-pkg-sheet-scroll hf-pkg-sheet-picker${
          searchFocused ? " hf-pkg-sheet-kb-open" : ""
        }`}
      >
        <div className="hf-pkg-sheet-handle" />
        {headerRow}
        {searchBar}
        <div className="hf-pkg-scroll-body">{resultsList}</div>
      </div>
    </div>
  );
};

export default MemberSelectorPopup;
